import * as React from "react";
import { useTranslation } from "react-i18next";
import { cn } from "@/lib/utils";

export interface PasswordStrengthIndicatorProps {
  /**
   * Password value to evaluate strength
   */
  password: string;
  /**
   * Additional CSS classes
   */
  className?: string;
}

/**
 * PasswordStrengthIndicator Component
 *
 * Displays visual password strength meter and requirements checklist.
 * Uses Tailwind theme colors with real-time validation feedback.
 *
 * @example
 * ```tsx
 * <PasswordStrengthIndicator password={password} />
 * ```
 */
export const PasswordStrengthIndicator = React.forwardRef<
  HTMLDivElement,
  PasswordStrengthIndicatorProps
>(({ password, className }, ref) => {
  const { t } = useTranslation("auth");

  // Password requirements
  const requirements = [
    {
      test: password.length >= 8,
      label: t("passwordReset.strength.minLength"),
    },
    {
      test: /[A-Z]/.test(password),
      label: t("passwordReset.strength.uppercase"),
    },
    {
      test: /[a-z]/.test(password),
      label: t("passwordReset.strength.lowercase"),
    },
    {
      test: /[0-9]/.test(password),
      label: t("passwordReset.strength.number"),
    },
  ];

  // Calculate strength (0-4)
  const strength = requirements.filter((r) => r.test).length;

  // Strength label and color
  const getStrengthConfig = () => {
    if (strength < 2) {
      return {
        label: t("passwordReset.strength.weak"),
        color: "bg-brand-red",
        percentage: 25,
      };
    }
    if (strength < 4) {
      return {
        label: t("passwordReset.strength.medium"),
        color: "bg-yellow-500",
        percentage: 50,
      };
    }
    return {
      label: t("passwordReset.strength.strong"),
      color: "bg-success",
      percentage: 100,
    };
  };

  const { label, color, percentage } = getStrengthConfig();

  if (!password) return null;

  return (
    <div ref={ref} className={cn("flex flex-col gap-2", className)}>
      {/* Strength meter */}
      <div className="flex items-center gap-2">
        <div className="flex-1 h-[0.25rem] bg-gray-100 rounded-full overflow-hidden">
          <div
            className={cn("h-full transition-all duration-300", color)}
            style={{ width: `${percentage}%` }}
          />
        </div>
        <span className="text-[0.875rem] text-gray-600 min-w-[4rem]">
          {label}
        </span>
      </div>

      {/* Requirements checklist */}
      <div className="flex flex-col gap-1">
        <p className="text-[0.75rem] text-gray-600 font-medium font-montserrat">
          {t("passwordReset.strength.title")}
        </p>
        <ul className="flex flex-col gap-1">
          {requirements.map((req, i) => (
            <li
              key={i}
              className={cn(
                "text-[0.75rem] flex items-center gap-1.5 font-montserrat",
                req.test ? "text-success" : "text-gray-300"
              )}
            >
              <span className="font-medium">{req.test ? "✓" : "○"}</span>
              {req.label}
            </li>
          ))}
        </ul>
      </div>
    </div>
  );
});

PasswordStrengthIndicator.displayName = "PasswordStrengthIndicator";
